/**
 * The browser's own install flow (Chromium: Android Chrome, desktop Chrome /
 * Edge). `beforeinstallprompt` can fire before React mounts, so this module
 * starts listening as soon as it is imported (main.tsx imports it first) and
 * keeps the event until the user taps Install. iOS has no install API: there
 * the app shows Add-to-Home-Screen steps instead.
 */

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => {
	for (const listener of listeners) listener();
};

if (typeof window !== "undefined") {
	window.addEventListener("beforeinstallprompt", (event) => {
		// Keep it for our own Install button instead of Chrome's mini-infobar.
		event.preventDefault();
		deferred = event as BeforeInstallPromptEvent;
		notify();
	});
	window.addEventListener("appinstalled", () => {
		deferred = null;
		installed = true;
		notify();
	});
}

/** For useSyncExternalStore: changes whenever install availability changes. */
export const installPromptSnapshot = (): string => `${deferred ? "ready" : "none"}|${installed ? "installed" : ""}`;

export const subscribeInstallPrompt = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};

export const canPromptInstall = (): boolean => deferred !== null;
export const wasInstalled = (): boolean => installed;

/**
 * Opens the browser's install dialog. Must run inside the tap handler:
 * `prompt()` is called synchronously, before anything is awaited, so the
 * browser still sees the user gesture.
 */
export const promptInstall = async (): Promise<InstallOutcome> => {
	const event = deferred;
	if (!event) return "unavailable";
	// One use per event: Chrome fires a new one if the user dismisses.
	deferred = null;
	const shown = event.prompt();
	notify();
	await shown;
	const { outcome } = await event.userChoice;
	return outcome;
};
